import { HttpContext, HttpContextToken } from '@angular/common/http';

export interface CacheSettings {
  lastUpdated?: Date;
  expires?: number | Date;
  update?: boolean;
}

export interface HypecastHttpContextOptions {
  skipApiPrefix?: boolean;
  skipErrorHandler?: boolean;
  skipAccessToken?: boolean;
  skipAuthorization?: boolean;
  /**
   * Keeps a 401 from this request out of the session token-refresh cascade.
   * Set it on calls to a host that does not accept this session's token at
   * all — a refresh cannot help there, and running one tears down a working
   * session (see SKIP_TOKEN_REFRESH below).
   */
  skipTokenRefresh?: boolean;
  cache?: true | CacheSettings;
  clearCache?: boolean;
}

export const SKIP_API_PREFIX = new HttpContextToken<boolean>(() => false);
export const SKIP_ERROR_HANDLER = new HttpContextToken<boolean>(() => false);
export const SKIP_ACCESS_TOKEN = new HttpContextToken<boolean>(() => false);
export const SKIP_AUTHORIZATION = new HttpContextToken<boolean>(() => false);
/**
 * A 401 normally means "this session's access token is stale", so the
 * authorization interceptor refreshes it and retries. That inference only
 * holds for hosts that actually accept our token. For a request to a service
 * with its own issuer, a 401 is structural: the refresh then fails, and the
 * failure takes the whole session down with it. Requests like that set this
 * flag and get their 401 passed straight through to the caller.
 */
export const SKIP_TOKEN_REFRESH = new HttpContextToken<boolean>(() => false);
export const CACHE_ENABLED = new HttpContextToken<boolean>(() => false);
export const CACHE_SETTINGS = new HttpContextToken<CacheSettings | null>(() => null);
export const CLEAR_HTTP_CACHE = new HttpContextToken<boolean>(() => false);

/**
 * Builds interceptor-only request metadata without leaking control flags into
 * headers, query parameters, or request bodies.
 */
export function createHttpContext(
  options: HypecastHttpContextOptions = {},
  context: HttpContext = new HttpContext(),
): HttpContext {
  if (options.skipApiPrefix) {
    context.set(SKIP_API_PREFIX, true);
  }
  if (options.skipErrorHandler) {
    context.set(SKIP_ERROR_HANDLER, true);
  }
  if (options.skipAccessToken) {
    context.set(SKIP_ACCESS_TOKEN, true);
  }
  if (options.skipAuthorization) {
    context.set(SKIP_AUTHORIZATION, true);
  }
  if (options.skipTokenRefresh) {
    context.set(SKIP_TOKEN_REFRESH, true);
  }
  if (options.cache) {
    context.set(CACHE_ENABLED, true);
    context.set(CACHE_SETTINGS, options.cache === true ? null : options.cache);
  }
  if (options.clearCache) {
    context.set(CLEAR_HTTP_CACHE, true);
  }

  return context;
}
